import { useEffect, useState } from "react";
import api from "../lib/api";
import BranchPicker from "../components/BranchPicker";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [xm, setXm] = useState(null);
  const [branchId, setBranchId] = useState(null);
  useEffect(() => {
    api.get("/analytics/dashboard", { params: { branch_id: branchId || undefined } }).then((r) => setD(r.data));
    api.get("/analytics/cross-model", { params: { branch_id: branchId || undefined } }).then((r) => setXm(r.data)).catch(() => {});
  }, [branchId]);
  if (!d) return <div className="p-8 text-slate-400">Loading dashboard…</div>;

  return (
    <div className="px-4 py-5 lg:px-8 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Management Dashboard</h1>
        <BranchPicker value={branchId} onChange={setBranchId} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Fleet", d.total_fleet], ["TDs this month", d.trips_this_month],
          [`Home TDs (${d.trips_this_month ? Math.round(d.home_td_this_month / d.trips_this_month * 100) : 0}%)`, d.home_td_this_month],
          ["Downtime %", `${d.downtime_pct}%`]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-heading text-2xl font-bold text-[#0E2240]">{v}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Fleet mix vs demand (30d)</h2>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.fleet_mix}>
                <XAxis dataKey="model" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="vehicles" fill="#0E2240" radius={[4, 4, 0, 0]} name="Vehicles" />
                <Bar dataKey="td_30d" fill="#BB162B" radius={[4, 4, 0, 0]} name="TDs 30d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {d.fleet_mix.length > 1 && (
            <p className="mt-2 text-[11px] text-slate-500">
              Rebalance hint: <b>{d.fleet_mix[0]?.model}</b> is in highest demand ({d.fleet_mix[0]?.td_per_vehicle} TD/vehicle); <b>{d.fleet_mix[d.fleet_mix.length - 1]?.model}</b> is lowest.
            </p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Km reconciliation — misuse flags</h2>
          <div className="mt-2 space-y-2">
            {d.km_reconciliation.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No unexplained km detected</div>}
            {d.km_reconciliation.map((r) => (
              <div key={r.reg_no} className="flex items-center justify-between rounded-lg bg-red-50 p-2.5 text-xs">
                <div><b className="text-[#0E2240]">{r.reg_no}</b> <span className="text-slate-500">odo +{r.odo_growth} km vs {r.trip_km} km in trips</span></div>
                <span className="font-bold text-[#BB162B]">+{r.unexplained_km} km unexplained</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Idle vehicles (≤1 TD in 30d)</h2>
          <div className="mt-2 space-y-1.5">
            {d.idle_vehicles.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No idle vehicles</div>}
            {d.idle_vehicles.map((r) => (
              <div key={r.reg_no} className="flex justify-between text-xs rounded-lg bg-slate-50 p-2.5">
                <span className="font-semibold text-[#0E2240]">{r.reg_no} · {r.model}</span>
                <span className="text-slate-500">{r.td_30d} TDs</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Demo ageing — buyback candidates</h2>
          <div className="mt-2 space-y-1.5">
            {d.ageing_candidates.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No vehicles past thresholds</div>}
            {d.ageing_candidates.map((r) => (
              <div key={r.reg_no} className="flex justify-between text-xs rounded-lg bg-amber-50 p-2.5">
                <span className="font-semibold text-[#0E2240]">{r.reg_no} · {r.model}</span>
                <span className="font-bold text-amber-700">{r.months_in_fleet} mo · {r.cum_km.toLocaleString("en-IN")} km</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="cross-model-card" className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Cross-model interest — additional model drives{xm ? ` (${xm.total})` : ""}</h2>
          <p className="mt-1 text-[11px] text-slate-400">Customers who drove a model different from their enquiry — direct demand-shift and upsell signal.</p>
          {(!xm || xm.pairs.length === 0) ? (
            <div className="text-xs text-slate-400 py-4 text-center">No additional model drives recorded yet</div>
          ) : (
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                {xm.pairs.slice(0, 8).map((p, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 p-2.5 text-xs">
                    <span className="font-semibold text-[#0E2240]">{p.enquiry_model} <span className="text-amber-600">→</span> {p.driven_model}</span>
                    <span className="font-bold text-[#BB162B]">{p.count}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                {xm.by_branch.slice(0, 8).map((b2, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 p-2.5 text-xs">
                    <span className="text-slate-600">{b2.branch}</span>
                    <span className="font-bold text-[#0E2240]">{b2.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
